import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { patientProfiles } from '../models/patientProfiles';
import { PatientProfile } from '../types/PatientProfile';
import { eq, and } from 'drizzle-orm';
import { BaseRepository } from '../../../classes/BaseRepository';
import { convertToAppError, AppError, AsyncResult, err, ok } from '@shared/utils';
import { HTTP_STATUS_NAMES, DATA_API_MESSAGES } from '@shared/constants';

/**
 * @group Patient
 * @summary Patient profile repository
 * 
 * <blockquote>
 * Provides data access methods for patient profile management, including CRUD operations and user-scoped access control.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods for patient profile validation.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const patientProfileRepository = new PatientProfileRepository();
 * const profile = await patientProfileRepository.getById('profile-uuid');
 * const userProfile = await patientProfileRepository.getProfileByUserId('user-uuid');
 * ```
 */
export class PatientProfileRepository extends BaseRepository<PatientProfile> {
  protected table = patientProfiles;

  /**
   * Get patient profile by user ID (one profile per user)
   * 
   * @param userId - The user ID to get profile for
   * @returns AsyncResult containing patient profile or error
   */
  async getProfileByUserId(userId: string): AsyncResult<PatientProfile | null, AppError> {
    if (!userId) {
      return err(convertToAppError(
        'User ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table).where(eq(this.table.user_id, userId))
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return ok(null);
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Create a new patient profile with validation
   * 
   * @param data - Patient profile data
   * @returns AsyncResult containing created profile or error
   */
  async create(data: Partial<PatientProfile>): AsyncResult<PatientProfile, AppError> {
    // Validate required fields
    const validationError = this.validatePatientProfileData(data);
    if (validationError) {
      return err(validationError);
    }

    // Check if user already has a profile
    if (data.user_id) {
      const existingProfileResult = await this.getProfileByUserId(data.user_id);
      if (existingProfileResult.isOk && existingProfileResult.getSuccess()) {
        return err(convertToAppError(
          'User already has a patient profile',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.CONFLICT
        ));
      }
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.insert(this.table).values(data as any).returning()
        );

        return result.match({
          ok: (rows) => ok(rows[0]),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Update patient profile with validation
   * 
   * @param id - Profile ID to update
   * @param data - Updated profile data
   * @returns AsyncResult containing updated profile or error
   */
  async update(id: string, data: Partial<PatientProfile>): AsyncResult<PatientProfile, AppError> {
    if (!id) {
      return err(convertToAppError(
        'Profile ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    // Validate data if provided
    if (Object.keys(data).length > 0) {
      const validationError = this.validatePatientProfileData(data, true);
      if (validationError) {
        return err(validationError);
      }
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.update(this.table)
            .set({
              ...data,
              date_modified: new Date()
            })
            .where(eq(this.table.id, id))
            .returning()
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return err(convertToAppError(
                `Patient profile not found with id: ${id}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get patient profile by phone number
   * 
   * @param phone - Phone number to search for
   * @returns AsyncResult containing patient profile or error
   */
  async getProfileByPhone(phone: string): AsyncResult<PatientProfile | null, AppError> {
    if (!phone) {
      return err(convertToAppError(
        'Phone number is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table).where(eq(this.table.phone, phone))
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return ok(null);
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Validate patient profile data
   * 
   * @param data - Patient profile data to validate
   * @param isUpdate - Whether this is an update operation (allows partial data)
   * @returns AppError if validation fails, null if valid
   */
  private validatePatientProfileData(data: Partial<PatientProfile>, isUpdate: boolean = false): AppError | null {
    // Required fields for creation
    if (!isUpdate) {
      if (!data.user_id) {
        return convertToAppError(
          'User ID is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
      if (!data.first_name) {
        return convertToAppError(
          'First name is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
      if (!data.last_name) {
        return convertToAppError(
          'Last name is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
      if (!data.date_of_birth) {
        return convertToAppError(
          'Date of birth is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
      if (!data.phone) {
        return convertToAppError(
          'Phone number is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
    }

    // Field length validation
    if (data.first_name && data.first_name.length > 64) {
      return convertToAppError(
        'First name must be 64 characters or less',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }
    if (data.last_name && data.last_name.length > 64) {
      return convertToAppError(
        'Last name must be 64 characters or less',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }
    if (data.phone && data.phone.length > 20) {
      return convertToAppError(
        'Phone number must be 20 characters or less',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }
    if (data.emergency_contact_name && data.emergency_contact_name.length > 64) {
      return convertToAppError(
        'Emergency contact name must be 64 characters or less',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }
    if (data.emergency_contact_phone && data.emergency_contact_phone.length > 20) {
      return convertToAppError(
        'Emergency contact phone must be 20 characters or less',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }

    // Date validation
    if (data.date_of_birth) {
      const birthDate = new Date(data.date_of_birth);
      const today = new Date();
      if (birthDate > today) {
        return convertToAppError(
          'Date of birth cannot be in the future',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
    }

    return null;
  }
}
